import { motion } from "framer-motion";
import { Play, Tv, Flame, Award, GraduationCap, BookOpen, Compass, ArrowRight, Lock } from "lucide-react";
import { SITE } from "../data/site";
import { trackEvent } from "@/lib/analytics";

// Single CTA target: when REACT_APP_SKOOL_URL is set, every "join / watch /
// preview" CTA on the Online Courses page routes there. Otherwise we route
// to the waitlist form so the funnel keeps working until Skool is ready.
const SKOOL_URL = process.env.REACT_APP_SKOOL_URL || "";
const SKOOL_LIVE = Boolean(SKOOL_URL);
const ONLINE_CTA = {
  href: SKOOL_LIVE ? SKOOL_URL : "/contact?focus=online&intent=waitlist",
  // External Skool needs target=_blank; internal waitlist stays in-tab.
  target: SKOOL_LIVE ? "_blank" : "_self",
  rel: SKOOL_LIVE ? "noreferrer" : undefined,
  primary: SKOOL_LIVE ? "Enter the Community" : "Join the Waitlist",
  secondary: SKOOL_LIVE ? "Start Watching" : "Preview",
  note: SKOOL_LIVE
    ? "Hosted on Skool · tutorials, community & on-demand training"
    : "USDD+ launches soon · Skool community + on-demand library",
};

const COURSES = [
  {
    icon: Tv,
    tag: "Digital Learning",
    title: "Foundations On-Demand",
    desc: "Self-paced street dance fundamentals — beginner-friendly, anywhere in the world.",
    modules: "24 modules",
    duration: "12h+",
    img: SITE.classImg2,
  },
  {
    icon: Flame,
    tag: "Freestyle",
    title: "Freestyle Development Lab",
    desc: "Build your own voice — drills, prompts and improvisation frameworks.",
    modules: "18 modules",
    duration: "9h",
    img: SITE.classImg3,
  },
  {
    icon: Award,
    tag: "Performance",
    title: "Performance Training",
    desc: "Stage craft, energy management and choreographic execution.",
    modules: "16 modules",
    duration: "8h",
    img: SITE.classImg4,
  },
  {
    icon: GraduationCap,
    tag: "Coaching",
    title: "Coaching Education",
    desc: "Become a confident dance teacher — pedagogy, safeguarding and lesson craft.",
    modules: "20 modules",
    duration: "11h",
    img: SITE.classImg1,
  },
  {
    icon: BookOpen,
    tag: "Culture",
    title: "Cultural Dance Education",
    desc: "Lineage, history and context — hip-hop, dancehall, afro, funk styles and more.",
    modules: "22 modules",
    duration: "14h",
    img: SITE.classImg5,
  },
  {
    icon: Compass,
    tag: "Creative",
    title: "Creative Development Pathway",
    desc: "From dancer to creator — concept, direction, content and career strategy.",
    modules: "26 modules",
    duration: "16h",
    img: SITE.classImg6,
  },
];

export const OnlineCourses = () => {
  return (
    <section
      id="online"
      data-testid="online-section"
      className="relative py-20 sm:py-24 overflow-hidden"
      style={{
        background:
          "radial-gradient(ellipse at top, #0d0617 0%, #050306 60%, #000 100%)",
      }}
    >
      {/* Cinematic spotlights */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(90,24,154,0.25),transparent_55%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(255,218,31,0.06),transparent_50%)]" />
      <div className="absolute inset-0 noise opacity-60 pointer-events-none" />

      {/* Scanline */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-yellow/40 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-brand-yellow/20 to-transparent" />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-5">
              <Tv size={18} className="text-brand-yellow" />
              <span className="text-xs font-bold uppercase tracking-[0.3em] text-brand-yellow">09 · Online Courses & On-Demand Training</span>
            </div>
            <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl text-white tracking-wide leading-[0.95]">
              <span className="italic text-brand-yellow text-glow-yellow">On-demand.</span>
              <br />
              On your time.
            </h2>
          </div>
          <p className="text-zinc-300/85 text-base max-w-md lg:text-right leading-relaxed">
            Cinematic, professionally produced courses you can stream anywhere —
            from foundations to creative direction. Built by working artists,
            paced for real life.
          </p>
        </div>

        {/* Featured hero tile */}
        <motion.a
          href={ONLINE_CTA.href}
          target={ONLINE_CTA.target}
          rel={ONLINE_CTA.rel}
          data-testid="featured-online-course"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="group relative block overflow-hidden rounded-3xl border border-white/10 hover:border-brand-yellow/40 transition-all mb-8"
        >
          <div className="relative aspect-[21/9] min-h-[300px]">
            <img
              src={SITE.hero}
              alt="USDD On-Demand"
              className="absolute inset-0 w-full h-full object-cover brightness-[0.4] saturate-110 group-hover:brightness-50 transition-all duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black via-black/60 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />

            <div className="absolute inset-0 flex items-center">
              <div className="px-8 sm:px-12 max-w-3xl">
                <span className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.3em] text-brand-yellow bg-black/60 backdrop-blur-md border border-brand-yellow/40 px-3 py-1.5 rounded-full">
                  Now Streaming · USDD+
                </span>
                <h3 className="mt-5 font-display text-4xl sm:text-6xl lg:text-7xl text-white tracking-wide leading-[0.95]">
                  The <span className="text-brand-yellow text-glow-yellow">Masters</span> Series.
                </h3>
                <p className="mt-4 text-sm sm:text-base text-zinc-300 max-w-xl">
                  Six legendary teachers. One global classroom. Cinematic
                  episodes covering technique, freestyle, performance and
                  culture — exclusive to USDD+.
                </p>
                <div className="mt-7 inline-flex items-center gap-3 flex-wrap">
                  <span className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand-yellow text-black font-black uppercase text-sm tracking-wider glow-yellow group-hover:scale-[1.03] transition-transform">
                    {SKOOL_LIVE ? <Play size={16} fill="currentColor" /> : <Lock size={14} />} {ONLINE_CTA.secondary}
                  </span>
                  <span className="text-[11px] uppercase tracking-[0.3em] text-zinc-400">
                    {ONLINE_CTA.note}
                  </span>
                </div>
              </div>
            </div>

            {/* big play orb */}
            <motion.div
              aria-hidden
              className="absolute right-8 md:right-16 top-1/2 -translate-y-1/2 hidden md:flex items-center justify-center w-24 h-24 rounded-full bg-white/10 backdrop-blur-xl border border-white/20 group-hover:bg-brand-yellow group-hover:border-brand-yellow transition-all"
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
            >
              <Play size={32} className="text-white group-hover:text-black transition-colors ml-1" fill="currentColor" />
            </motion.div>
          </div>
        </motion.a>

        {/* Course rail */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {COURSES.map((c, i) => (
            <motion.a
              key={c.title}
              href={ONLINE_CTA.href}
              target={ONLINE_CTA.target}
              rel={ONLINE_CTA.rel}
              data-testid={`online-course-${i}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: i * 0.06 }}
              className="group relative overflow-hidden rounded-2xl border border-white/10 hover:border-brand-yellow/50 transition-all bg-zinc-950/60"
            >
              <div className="relative aspect-video overflow-hidden">
                <img
                  src={c.img}
                  alt={c.title}
                  className="absolute inset-0 w-full h-full object-cover brightness-[0.55] group-hover:brightness-75 group-hover:scale-105 transition-all duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />

                {/* play overlay */}
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="w-14 h-14 rounded-full bg-brand-yellow/95 flex items-center justify-center">
                    <Play size={20} className="text-black ml-1" fill="currentColor" />
                  </div>
                </div>

                <span className="absolute top-3 left-3 text-[9px] font-bold uppercase tracking-[0.3em] text-brand-yellow bg-black/70 backdrop-blur px-2.5 py-1 rounded-sm border-l-2 border-brand-yellow">
                  {c.tag}
                </span>
                <span className="absolute bottom-3 right-3 text-[10px] uppercase tracking-wider text-white bg-black/70 backdrop-blur px-2 py-1 rounded-sm font-bold">
                  {c.duration}
                </span>
              </div>

              <div className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <c.icon size={14} className="text-brand-yellow" />
                  <span className="text-[9px] uppercase tracking-[0.25em] text-zinc-400">{c.modules}</span>
                </div>
                <h3 className="font-display text-xl text-white tracking-wide leading-tight">{c.title}</h3>
                <p className="mt-1.5 text-[11px] text-zinc-400 leading-relaxed line-clamp-2">{c.desc}</p>
                <div className="mt-3 pt-2.5 border-t border-white/10 flex items-center justify-between text-[10px] uppercase tracking-[0.25em]">
                  <span className="text-zinc-500">USDD+</span>
                  <span className="text-brand-yellow font-bold inline-flex items-center gap-1">
                    Preview <ArrowRight size={11} />
                  </span>
                </div>
              </div>
            </motion.a>
          ))}
        </div>

        {/* Footer CTA */}
        <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-6 rounded-2xl bg-white/[0.03] border border-white/10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-brand-yellow mb-1">USDD+ Membership</div>
            <div className="font-display text-2xl text-white tracking-wide">
              {SKOOL_LIVE
                ? "Unlimited streaming. Live community. New drops monthly."
                : "Unlimited streaming. New drops monthly."}
            </div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500 mt-1">{ONLINE_CTA.note}</div>
          </div>
          <a
            href={ONLINE_CTA.href}
            target={ONLINE_CTA.target}
            rel={ONLINE_CTA.rel}
            data-testid="online-join-cta"
            onClick={() => trackEvent("online_interest_click", { destination: SKOOL_LIVE ? "skool" : "waitlist", placement: "footer-cta" })}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand-yellow text-black font-black uppercase text-sm tracking-wider hover:bg-brand-yellowHover hover:scale-[1.03] transition-all glow-yellow"
          >
            {ONLINE_CTA.primary} <ArrowRight size={15} />
          </a>
        </div>
      </div>
    </section>
  );
};
